import React, { useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import Grid from '@material-ui/core/Grid'
import { useIntl } from 'react-intl'
import messages from '@utils/messages'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Button } from '@material-ui/core'
import DialogActions from '@material-ui/core/DialogActions'
import clsx from 'clsx'
import { useCookies, AppCookies } from '@src/utils/cookies'
import { useEmailVerificationMutation } from '@src/mutations/EmailVerificationMutation.generated'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { useCurrentUser } from '@utils/useCurrentUser'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import { EmailLinkVerificationActionEnum as emailVerifcationGAActions } from '@utils/analytics/enums'

const useAccountStyles = makeStyles((theme: Theme) => ({
  root: {
    maxHeight: '550px',
    width: '510px',
    maxWidth: '620px',
    height: '280px',
    [theme.breakpoints.down('xs')]: {
      width: '320px',
      maxHeight: '568px',
      flexGrow: 0,
      padding: '16px 16px 18.5px 16px',
    },
  },

  closeButton: {
    position: 'absolute',
    right: '32px',
    top: '32px',
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      top: 16,
      right: 16,
    },
  },
  headerTextContainer: {
    padding: '32px 32px 16px 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '0 0 16px 0',
    },
    '& h2': {
      display: 'flex',
      alignItems: 'center',
      textTransform: 'uppercase',
      fontSize: '20px',
      fontWeight: '900',
      lineHeight: 'normal',
      letterSpacing: '1px',
    },
  },
  cancelBtn: {
    marginRight: '8px',
    [theme.breakpoints.down('xs')]: {
      display: 'none !important',
    },
  },
  dialogContent: {
    marginBottom: '8px',
    marginRight: '4px',
    padding: '0 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '0 0 16.5px 0',
      margin: 0,
    },
  },
  mobileButton: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      width: '100%',
      height: 47,
      marginLeft: '0 !important',
    },
  },
  dialogActions: {
    margin: '0 24px 24px 0',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      flexDirection: 'column',
    },
  },
}))

const EmailVerificationModal = () => {
  const classes = useAccountStyles()
  const { formatMessage } = useIntl()
  const [emailModalOpen, setEmailModalOpen] = useState(true)
  const [emailVerificationMutation, { loading }] =
    useEmailVerificationMutation()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const intl = useIntl()
  const { userId } = useCurrentUser()
  const [, setCookies] = useCookies()

  const snoozeEmailNotification = () => {
    const d = new Date()
    const minutes = 24 * 60 * 60 * 1000
    d.setTime(d.getTime() + minutes)
    setCookies(`${AppCookies.HideEmailVerifionModal}_${userId}`, true, {
      path: '/',
      expires: d,
    })
    setEmailModalOpen(false)
  }

  const emailVerification = async () => {
    try {
      const message = intl.formatMessage(
        messages.RESEND_EMAIL_NOTIFICATION_SNACBAR
      )
      await emailVerificationMutation()
      setGlobalSnackbarState({
        open: true,
        message,
        variant: SnackbarType.Success,
      })
      sendCommonDetailClickEvent(
        {
          event: 'profile_interaction',
          action: 'send email verification',
          detail: undefined,
          section: 'verify your email address',
          component: 'modal',
          elementType: 'button',
          elementText: 'verify email',
          coreEvent: 'no',
        },
        {
          eventCategory: 'email verification',
          eventAction: emailVerifcationGAActions.VerificationSend,
          eventLabel: 'email verification modal',
          eventInteractionType: 0,
        }
      )
      snoozeEmailNotification()
    } catch (e) {
      const message = intl.formatMessage(messages.GENERIC_ERROR)

      setGlobalSnackbarState({
        open: true,
        message,
        variant: SnackbarType.Error,
      })
    }
  }

  return (
    <Dialog
      classes={{ paperWidthSm: classes.root }}
      style={{ zIndex: 1301 }}
      open={emailModalOpen}
    >
      <IconButton
        onClick={snoozeEmailNotification}
        className={classes.closeButton}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogTitle className={classes.headerTextContainer}>
        {formatMessage(messages.EMAIL_VERIFY)}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container alignItems="flex-end" style={{ maxHeight: '333px' }}>
          {formatMessage(messages.EMAIL_VERIFY_SUB)}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={snoozeEmailNotification}
          disabled={loading}
          className={clsx(classes.mobileButton, classes.cancelBtn)}
        >
          {formatMessage(messages.REMIND_ME_LATER)}
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            emailVerification()
          }}
          className={clsx(classes.mobileButton)}
        >
          {formatMessage(messages.VERIFY_MAIL)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailVerificationModal
